import i18n from "../../util/i18n.ts";

/**
 * Y-08: radar chart for the 14 current ratings — replaces the wide
 * sortable table on mobile so a thumb-sized screen still shows the
 * whole rating shape at a glance.
 *
 * Pure SVG because all we need is 14 line segments + one filled polygon.
 */

type Props = {
	ratings: string[];
	values: Record<string, number>;
	ovr?: number;
	pot?: number;
};

const SIZE = 280;
const CENTER = SIZE / 2;
const RADIUS = SIZE / 2 - 36;
const RINGS = [0.25, 0.5, 0.75, 1];

const RATING_LABEL_KEYS: Record<string, string> = {
	hgt: "height",
	stre: "strength",
	spd: "speed",
	jmp: "jumping",
	endu: "endurance",
	ins: "inside",
	dnk: "layups_short",
	ft: "freeThrows_short",
	fg: "midRange",
	tp: "threePointers_short",
	oiq: "offIQ_short",
	diq: "defIQ_short",
	drb: "dribbling",
	pss: "passing",
	reb: "rebounding_short",
};

const ratingLabel = (key: string) => {
	const labelKey = RATING_LABEL_KEYS[key];
	if (labelKey) {
		return i18n.t(`ratingsOverview.${labelKey}`, {
			ns: "ui",
			defaultValue: key,
		});
	}

	return i18n.t(`rating_${key}`, { ns: "cols", defaultValue: key });
};

export const RatingsRadar = ({ ratings, values, ovr, pot }: Props) => {
	const n = ratings.length;
	if (n === 0) {
		return null;
	}

	const angle = (i: number) => (i / n) * Math.PI * 2 - Math.PI / 2;

	const point = (i: number, r: number) => {
		const a = angle(i);
		return [CENTER + Math.cos(a) * r, CENTER + Math.sin(a) * r] as const;
	};

	const polygon = ratings
		.map((key, i) => {
			const v = Math.max(0, Math.min(100, values[key] ?? 0)) / 100;
			const [x, y] = point(i, RADIUS * v);
			return `${x},${y}`;
		})
		.join(" ");

	return (
		<div className="d-flex flex-column align-items-center my-2">
			<svg
				width={SIZE}
				height={SIZE}
				viewBox={`0 0 ${SIZE} ${SIZE}`}
				role="img"
				aria-label={i18n.t("playerProfile.ratingsRadarLabel", {
					ns: "ui",
					defaultValue: "Player ratings radar",
				})}
			>
				{RINGS.map((r) => (
					<polygon
						key={r}
						points={ratings
							.map((_, i) => {
								const [x, y] = point(i, RADIUS * r);
								return `${x},${y}`;
							})
							.join(" ")}
						fill="none"
						stroke="var(--bs-border-color)"
						strokeWidth={r === 1 ? 1.5 : 0.5}
						opacity={0.6}
					/>
				))}

				{ratings.map((_, i) => {
					const [x, y] = point(i, RADIUS);
					return (
						<line
							key={i}
							x1={CENTER}
							y1={CENTER}
							x2={x}
							y2={y}
							stroke="var(--bs-border-color)"
							strokeWidth={0.5}
							opacity={0.5}
						/>
					);
				})}

				<polygon
					points={polygon}
					fill="var(--bs-primary)"
					fillOpacity={0.25}
					stroke="var(--bs-primary)"
					strokeWidth={1.5}
				/>

				{ratings.map((key, i) => {
					const [x, y] = point(i, RADIUS + 14);
					return (
						<text
							key={key}
							x={x}
							y={y}
							textAnchor="middle"
							dominantBaseline="middle"
							fontSize={10}
							fill="var(--bs-body-color)"
							fontWeight={600}
						>
							{ratingLabel(key)}
						</text>
					);
				})}
			</svg>

			{ovr !== undefined || pot !== undefined ? (
				<div className="d-flex gap-4 mt-1">
					{ovr !== undefined ? (
						<div className="text-center">
							<div className="small text-body-secondary">
								{i18n.t("title.Ovr", { ns: "cols", defaultValue: "Overall" })}
							</div>
							<div className="fs-4 fw-bold">{ovr}</div>
						</div>
					) : null}
					{pot !== undefined ? (
						<div className="text-center">
							<div className="small text-body-secondary">
								{i18n.t("title.Pot", { ns: "cols", defaultValue: "Potential" })}
							</div>
							<div className="fs-4 fw-bold">{pot}</div>
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
};

export default RatingsRadar;
